import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  SORT_OPTIONS,
  applyFilters,
  emptyFilters,
  movieItems,
  sortItems,
  tvItems,
  type CatalogItem,
  type FilterState,
  type SortKey,
} from "@/utils/catalog";

const PAGE_SIZE = 8;

interface CategoryPageProps {
  category: "movie" | "tvseries";
  h1: string;
  description: string;
  categoryLabel: string;
  path: string;
  keywords: string;
}

export default function CategoryPage({
  category,
  h1,
  description,
  categoryLabel,
  path,
  keywords,
}: CategoryPageProps) {
  const source = category === "movie" ? movieItems : tvItems;
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sort, setSort] = useState<SortKey>("newest");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  usePageMeta({
    title: `${categoryLabel}分类 - 可可影视`,
    description,
    keywords,
    canonical: `https://hbsfkj.cn${path}`,
  });

  const results = useMemo(() => sortItems(applyFilters(source, filters), sort), [source, filters, sort]);
  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pagedItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const resetFilters = () => setFilters(emptyFilters());

  const sortControl = (
    <div className="flex items-center gap-1 rounded-full border border-background-200/80 bg-background-100 p-1">
      {SORT_OPTIONS.map((option) => (
        <button
          key={option.key}
          type="button"
          onClick={() => setSort(option.key)}
          className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
            sort === option.key
              ? "bg-primary-600 text-background-50"
              : "text-foreground-600 hover:text-primary-700"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1">
        <div className="w-full border-b border-background-200/80 bg-background-100 px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: categoryLabel }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{h1}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {description}
          </p>
        </div>

        <div className="w-full px-4 py-8 md:px-8 md:py-10">
          <div className="flex flex-col gap-8 lg:flex-row">
            <aside className="hidden w-64 shrink-0 lg:block">
              <FilterPanel filters={filters} onChange={setFilters} onReset={resetFilters} />
            </aside>

            <div className="flex-1">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-foreground-600">
                  共 <b className="font-semibold text-foreground-900">{results.length}</b> 部作品
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMobileFilterOpen(true)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-xs font-medium text-foreground-700 lg:hidden cursor-pointer"
                  >
                    <i className="ri-filter-3-line"></i>
                    筛选
                  </button>
                  {sortControl}
                </div>
              </div>

              {pagedItems.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {pagedItems.map((item: CatalogItem) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
                    <i className="ri-film-line"></i>
                  </span>
                  <p className="mt-5 font-heading text-lg font-bold text-foreground-900">
                    未找到符合条件的影视作品
                  </p>
                  <p className="mt-2 text-sm text-foreground-500">试试放宽筛选条件，或重置后重新浏览。</p>
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
                  >
                    重置筛选
                  </button>
                </div>
              )}

              <Pagination current={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </div>
        </div>
      </main>
      <Footer />

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-background-50 lg:hidden">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-4">
            <h2 className="font-heading text-lg font-bold text-foreground-950">筛选条件</h2>
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setMobileFilterOpen(false)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <FilterPanel filters={filters} onChange={setFilters} onReset={resetFilters} />
          </div>
          <div className="border-t border-background-200 p-4">
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="w-full whitespace-nowrap rounded-md bg-primary-600 py-3 text-sm font-medium text-background-50 cursor-pointer"
            >
              查看 {results.length} 部作品
            </button>
          </div>
        </div>
      )}
    </div>
  );
}